import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import CloseIcon from "@mui/icons-material/Close";

const PRINTERTOOLS_URL = "http://localhost:4000";

export default function ModalEnrolarHuella({
  open,
  usuario,
  perfil,
  seguridadUrl,
  administrativoUrl,
  systemAccessToken = "",
  tenantAccessToken = "",
  onClose,
  onCompleted,
}) {
  const [estado, setEstado] = useState("esperando");
  const [mensaje, setMensaje] = useState("Preparando lector");
  const [muestra, setMuestra] = useState(0);
  const [total, setTotal] = useState(4);
  const [finalizado, setFinalizado] = useState(false);
  const completadoRef = useRef(false);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    if (!open || !usuario?.id || !usuario?.usuario) return undefined;

    let activo = true;
    const controller = new AbortController();
    completadoRef.current = false;
    setEstado("esperando");
    setMensaje("Preparando lector");
    setMuestra(0);
    setFinalizado(false);

    const payload = {
      perfil,
      accesoId: Number(usuario.id),
      usuario: usuario.usuario,
      nombre: usuario.nombre,
      seguridadUrl,
      administrativoUrl,
      systemAccessToken,
      tenantAccessToken,
    };

    const esperarCaptura = async () => {
      while (activo && !completadoRef.current) {
        let respuesta;
        try {
          respuesta = await fetch(`${PRINTERTOOLS_URL}/api/huella/enroll/wait`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });
        } catch (error) {
          if (error.name === "AbortError") return;
          throw new Error("PrinterTools no está disponible en esta estación");
        }

        if (respuesta.status === 204) {
          setEstado("esperando");
          setMensaje("Esperando lectura del DigitalPersona");
          continue;
        }

        const contenido = await respuesta.json();
        if (!respuesta.ok || !contenido.success) {
          throw new Error(contenido.message || "No fue posible capturar la huella");
        }

        const enrolamiento = contenido.enrolamiento || {};
        const muestrasTomadas = Number(enrolamiento.muestra || 0);
        const muestrasTotales = Number(enrolamiento.total || 4);
        setMuestra(muestrasTomadas);
        setTotal(muestrasTotales);
        setMensaje(contenido.message || enrolamiento.mensaje || "Muestra recibida");

        if (enrolamiento.terminado) {
          completadoRef.current = true;
          setEstado("completado");
          setFinalizado(true);
          onCompletedRef.current?.(enrolamiento);
        } else {
          setEstado("capturando");
        }
      }
    };

    esperarCaptura().catch((error) => {
      if (!activo) return;
      setEstado("error");
      setMensaje(error.message || "No fue posible completar el enrolamiento");
    });

    return () => {
      activo = false;
      controller.abort();
    };
  }, [
    open,
    usuario,
    perfil,
    seguridadUrl,
    administrativoUrl,
    systemAccessToken,
    tenantAccessToken,
  ]);

  const cerrar = async () => {
    try {
      await fetch(`${PRINTERTOOLS_URL}/api/huella/enroll/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
        keepalive: true,
      });
    } catch {
      // El cierre del modal no depende de la disponibilidad del agente local.
    }
    onClose?.(finalizado);
  };

  const progreso = total > 0 ? Math.min(100, (muestra / total) * 100) : 0;

  return (
    <Dialog open={open} onClose={cerrar} fullWidth maxWidth="sm">
      <DialogTitle>Enrolar huella</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                display: "grid",
                placeItems: "center",
                border: "1px solid",
                borderColor: estado === "error" ? "error.main" : "primary.main",
                borderRadius: 1,
              }}
            >
              <FingerprintIcon color={estado === "error" ? "error" : "primary"} sx={{ fontSize: 42 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                {usuario?.usuario} - {usuario?.nombre}
              </Typography>
              <Typography variant="body2" color="text.secondary">{mensaje}</Typography>
            </Box>
          </Stack>

          {estado === "error" ? (
            <Alert severity="error">{mensaje}</Alert>
          ) : estado === "completado" ? (
            <Alert severity="success">Huella guardada correctamente</Alert>
          ) : (
            <Box>
              <LinearProgress variant="determinate" value={progreso} />
              <Typography variant="caption" color="text.secondary">
                Muestras recibidas: {muestra} de {total}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={cerrar} startIcon={<CloseIcon />}>
          {finalizado ? "Cerrar" : "Cancelar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
